import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "utils";

export const productList = createAsyncThunk('product/list', async ({ pageable: data, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `product`,
            params: {
                'pageSize': data?.pageSize,
                'pageNo': data?.currentPage,
                'search': data?.search,
                'category_id': data?.category_id,
                'type': data?.type,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const productDetails = createAsyncThunk('product/details', async ({ id, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `product/${id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addProduct = createAsyncThunk('product/add', async (data) => {

    let nutrition_parameters = data?.values?.nutrition_parameters?.map(val => ({
        "title": val?.title,
        "value": val?.value,
        "percentage": val?.percentage,
    }))

    let ingredient_parameters = data?.values?.ingredient_parameters?.map(val => ({
        "ingredient": val?.ingredient,
        "ingredient_description": val?.ingredient_description,
    }))

    let product_variants = data?.values?.product_variants?.map(val => ({
        "variant_value": val?.value,
        "unit_id": val?.unit_id,
    }))

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'product',
            data: {
                "category_id": data?.values?.category_id,
                "product_name": data?.values?.name,
                "product_description": data?.values?.description,
                "product_type": data?.values?.type,
                "status": data?.values?.status,
                "product_reorder": data?.values?.reorder_level,
                "is_hero": Boolean(data?.values?.hero_product),
                "product_variants": product_variants,
                "nutrition_parameters": nutrition_parameters,
                "ingredient_parameters": ingredient_parameters,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updateProduct = createAsyncThunk('product/update', async (data) => {

    let nutrition_parameters = data?.values?.nutrition_parameters?.map(val => ({
        "nutrition_id": val?.id,
        "title": val?.title,
        "value": val?.value,
        "percentage": val?.percentage,
    }))

    let ingredient_parameters = data?.values?.ingredient_parameters?.map(val => ({
        "ingredient_id": val?.id,
        "ingredient": val?.ingredient,
        "ingredient_description": val?.ingredient_description,
    }))

    let product_variants = data?.values?.product_variants?.map(val => ({
        "variant_id": val?.id,
        "variant_value": val?.value,
        "unit_id": val?.unit_id,
    }))

    try {
        const res = await axios({
            method: 'PUT',
            url: getConfig.API_URL + 'product',
            data: {
                "product_id": data?.values?.id,
                "category_id": data?.values?.category_id,
                "product_name": data?.values?.name,
                "product_description": data?.values?.description,
                "product_type": data?.values?.type,
                "status": data?.values?.status,
                "product_reorder": data?.values?.reorder_level,
                "is_hero": Boolean(data?.values?.hero_product),
                "product_variants": product_variants,
                "nutrition_parameters": nutrition_parameters,
                "ingredient_parameters": ingredient_parameters,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const delProduct = createAsyncThunk('product/delete', async (data) => {
    try {
        const res = await axios({
            method: 'DELETE',
            url: getConfig.API_URL + `product/${data?.id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addProductImage = createAsyncThunk('product/image/add', async (data) => {
    const bodyFormData = new FormData()
    bodyFormData.append('product_id', data?.values?.product_id)
    bodyFormData.append('product_image', data?.values?.image)
    bodyFormData.append('image_priority', data?.values?.priority)

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'product_image',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "product_image", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updateProductImage = createAsyncThunk('product/image/update', async (data) => {
    const bodyFormData = new FormData()
    bodyFormData.append('image_id', data?.values?.id)
    bodyFormData.append('product_id', data?.values?.product_id)
    bodyFormData.append('image_priority', data?.values?.priority)
    if (data?.values?.image) {
        bodyFormData.append('product_image', data.values.image)
    }

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'update_product_image',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "product_image", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const delProductImage = createAsyncThunk('product/image/delete', async (data) => {
    try {
        const res = await axios({
            method: 'DELETE',
            url: getConfig.API_URL + `product_image/${data?.id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addProductHeroImage = createAsyncThunk('product/hero-image/add', async (data) => {
    const bodyFormData = new FormData()
    bodyFormData.append('product_id', data?.values?.product_id)
    bodyFormData.append('hero_image', data?.values?.hero_image)

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'product_hero_image',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "hero_image", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

const product = createSlice({
    name: 'product',
    initialState: {
        list: [],
        details: null,
        loading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(productList.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.list = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.list = []
            }
        });

        builder.addCase(productDetails.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.details = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.details = null
            }
        });
    }
})

export default product.reducer;